/**
 * GEX Strike Path Card — primary visualization for the Wheel ITM cycle.
 *
 * Spec: /tmp/gex_strike_path.md (replaces the classic GEX panel as the
 * default operator surface). The legacy curve / by-strike chart stays
 * available as an "Advanced View" toggle inside GEXPanel.
 *
 * Reads `data.strike_path` produced by the backend (gex_strike_path.py)
 * and renders three panels:
 *   1. Compact Operator Card  (1-glance summary, spec §13.3)
 *   2. Strike Path Map       (horizontal SVG, spec §13.1)
 *   3. Wall Matrix table     (spec §13.2)
 *
 * The user can change the cycle objective via a dropdown — that re-fires
 * the GEX endpoint with `?objective=…` so all interpretation flips live.
 */
import React from "react";

const FAMILY_COLOR = {
  HELP:     "#00FF88",
  BLOCK:    "#FF3366",
  TRIGGER:  "#FFB800",
  DANGER:   "#FF3366",
  NEUTRAL:  "#8A8A93",
  SPOT:     "#3BB5FF",
  TARGET:   "#A855F7",
  FRICTION: "#FFB800",
};

const PATH_STATUS_LABEL = {
  CLEAR:        "Path Clear",
  FRICTION:     "Path Friction",
  BLOCKED:      "Path Blocked",
  TRIGGER_FUEL: "Trigger / Fuel",
};

const DOWNSIDE_STATUS_LABEL = {
  CUSHION: "Cushion",
  FRAGILE: "Fragile",
  DANGER:  "Danger",
  NONE:    "—",
};

const OBJECTIVE_OPTIONS = [
  { v: "CSP_PREMIUM_ROTATION",           label: "CSP — Premium Rotation" },
  { v: "CSP_ASSIGNMENT_ACCEPTED",        label: "CSP — Assignment Accepted" },
  { v: "ASSIGNED_INVENTORY_CC_PLANNING", label: "Assigned Inventory — CC Planning" },
  { v: "CC_CYCLE_CLOSURE",               label: "CC — Cycle Closure" },
  { v: "CC_PREMIUM_HARVEST",             label: "CC — Premium Harvest" },
  { v: "WAIT_OBSERVE",                   label: "Wait / Observe" },
];

const fmtMoney = (v) => {
  if (v == null) return "—";
  const sign = v >= 0 ? "+" : "-";
  const a = Math.abs(v);
  if (a >= 1e6) return `${sign}${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}${(a / 1e3).toFixed(0)}K`;
  return `${sign}${a.toFixed(1)}`;
};

// Stable equality for memoization — only re-render when the meaningful
// shape of the strike-path payload actually changes. The parent polls
// every 60s and produces a new object reference each time even when the
// underlying data is identical, which caused visible flickering of the
// SVG map / matrix table. JSON-stringifying `sp` is fine here: payload
// is small (<2 KB) and infrequent (60s).
const _spEqual = (prev, next) => {
  try { return JSON.stringify(prev.sp) === JSON.stringify(next.sp); }
  catch { return false; }
};

function OperatorCardImpl({ sp }) {
  const impactColor = (
    sp.objective_impact === "HELPS"   ? "#00FF88" :
    sp.objective_impact === "BLOCKS"  ? "#FF3366" :
    sp.objective_impact === "DANGER"  ? "#FF3366" :
    sp.objective_impact === "TRIGGER" ? "#FFB800" : "#8A8A93"
  );
  const roiColor = sp.roi_gate_pass === true ? "#00FF88" :
                   sp.roi_gate_pass === false ? "#FF3366" : "#8A8A93";
  return (
    <div data-testid="gex-strike-path-operator-card"
         className="bg-[#08080A] border border-[#26262B] rounded-sm p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div data-testid="strike-path-objective-label"
             className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#3BB5FF]">
          {sp.objective_label}
        </div>
        <div data-testid="strike-path-objective-impact"
             className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm"
             style={{ color: impactColor, backgroundColor: `${impactColor}1A`, border: `1px solid ${impactColor}66` }}>
          {sp.objective_impact}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] font-mono">
        <div className="text-[#8A8A93]">Path:</div>
        <div className="text-white">${sp.spot} → ${sp.target_strike}</div>
        <div className="text-[#8A8A93]">Status:</div>
        <div data-testid="strike-path-status" style={{ color: FAMILY_COLOR[
          sp.path_status === "CLEAR" ? "HELP" :
          sp.path_status === "BLOCKED" ? "BLOCK" :
          sp.path_status === "TRIGGER_FUEL" ? "TRIGGER" : "FRICTION"
        ]}}>
          {PATH_STATUS_LABEL[sp.path_status] || sp.path_status}
        </div>
        <div className="text-[#8A8A93]">Downside:</div>
        <div data-testid="strike-path-downside" style={{ color: FAMILY_COLOR[
          sp.downside_status === "CUSHION" ? "HELP" :
          sp.downside_status === "DANGER" ? "DANGER" : "FRICTION"
        ]}}>
          {DOWNSIDE_STATUS_LABEL[sp.downside_status] || sp.downside_status}
        </div>
        {sp.roi_cycle_pct != null && (
          <>
            <div className="text-[#8A8A93]">ROI cycle:</div>
            <div data-testid="strike-path-roi" style={{ color: roiColor }}>
              {sp.roi_cycle_pct.toFixed(2)}% {sp.roi_gate_pass ? "·  PASS" : "·  FAIL"}
            </div>
          </>
        )}
      </div>
      <div data-testid="strike-path-decision-support"
           className="text-[10px] text-[#E4E4E7] leading-snug border-t border-[#26262B] pt-2">
        {sp.decision_support}
      </div>
      {(sp.warnings || []).map((w, i) => (
        <div key={i} className="text-[10px] text-[#FF3366] font-mono">⚠ {w}</div>
      ))}
    </div>
  );
}

function StrikePathMapImpl({ sp }) {
  // Domain: from min(spot, target, primary_put_wall) to max(spot, target, call_wall)
  const xs = [sp.spot, sp.target_strike];
  if (sp.primary_put_wall) xs.push(sp.primary_put_wall.strike);
  if (sp.primary_call_wall) xs.push(sp.primary_call_wall.strike);
  (sp.path_walls || []).forEach(w => xs.push(w.strike));
  (sp.downside_walls || []).slice(0, 2).forEach(w => xs.push(w.strike));
  const lo = Math.min(...xs);
  const hi = Math.max(...xs);
  const W = 520, H = 110, padX = 30, padY = 32;
  const xFor = (v) => padX + ((v - lo) / Math.max(hi - lo, 1e-6)) * (W - 2 * padX);
  const trackY = H / 2 + 2;
  // Path zone shading from spot to target
  const pathColor = FAMILY_COLOR[
    sp.path_status === "CLEAR" ? "HELP" :
    sp.path_status === "BLOCKED" ? "BLOCK" :
    sp.path_status === "TRIGGER_FUEL" ? "TRIGGER" : "FRICTION"
  ];
  const pathFromX = xFor(Math.min(sp.spot, sp.target_strike));
  const pathToX   = xFor(Math.max(sp.spot, sp.target_strike));

  // Build markers array, then stagger when consecutive labels overlap (<28 px apart).
  // Each marker: {x, color, label, sublabel, side, key}
  const markers = [];
  (sp.downside_walls || []).slice(0, 2).forEach((w, i) => markers.push({
    x: xFor(w.strike), color: FAMILY_COLOR[w.family],
    label: `$${w.strike}`,
    sublabel: w.gex_sign === "POSITIVE" ? "Cushion" : "Danger",
    side: "bottom", key: `dw-${i}`,
  }));
  (sp.path_walls || []).slice(0, 4).forEach((w, i) => markers.push({
    x: xFor(w.strike), color: FAMILY_COLOR[w.family],
    label: `$${w.strike}`,
    sublabel: w.gex_sign === "POSITIVE" ? "Friction" : "Trigger",
    side: "top", key: `pw-${i}`,
  }));
  markers.push({
    x: xFor(sp.spot), color: FAMILY_COLOR.SPOT,
    label: "Spot", sublabel: `$${sp.spot}`, side: "top", key: "spot",
  });
  markers.push({
    x: xFor(sp.target_strike), color: FAMILY_COLOR.TARGET,
    label: sp.target_type === "CC_STRIKE" ? "CC" : "CSP",
    sublabel: `$${sp.target_strike}`, side: "bottom", key: "target",
  });
  // Stagger labels per side: when two markers of the same side are <28 px
  // apart on x, push the second one's label up/down by 14px.
  const sortedTop = markers.filter(m => m.side === "top").sort((a, b) => a.x - b.x);
  const sortedBot = markers.filter(m => m.side === "bottom").sort((a, b) => a.x - b.x);
  const stagger = (arr, baseY, dir) => {
    let lastX = -999, level = 0;
    arr.forEach(m => {
      level = (Math.abs(m.x - lastX) < 28) ? (level + 1) : 0;
      m.labelY = baseY + dir * level * 13;
      m.sublabelY = m.labelY + dir * (-12);
      lastX = m.x;
    });
  };
  stagger(sortedTop, padY - 6, -1);     // grow upward
  stagger(sortedBot, H - padY + 14, 1); // grow downward

  return (
    <div data-testid="gex-strike-path-map" className="bg-[#121216] border border-[#26262B] rounded-sm p-3">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#8A8A93]">
          Strike Path Map
        </div>
        <div data-testid="strike-path-map-objective"
             className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#3BB5FF] truncate ml-2 max-w-[60%]"
             title={sp.objective_label}>
          {sp.objective_label}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none">
        {/* Path zone shading */}
        <rect x={pathFromX} y={trackY - 8} width={pathToX - pathFromX} height={16}
              fill={pathColor} fillOpacity="0.15" />
        {/* Track line */}
        <line x1={padX} x2={W - padX} y1={trackY} y2={trackY}
              stroke="#26262B" strokeWidth="1.5" />
        {markers.map(m => (
          <g key={m.key}>
            <line x1={m.x} x2={m.x} y1={padY} y2={H - padY} stroke={m.color} strokeWidth="1.2" opacity="0.8" />
            <circle cx={m.x} cy={trackY} r={4} fill={m.color} stroke="#08080A" strokeWidth="1.5" />
            <text x={m.x} y={m.labelY} fontSize="10" fontFamily="monospace"
                  textAnchor="middle" fill={m.color} fontWeight="bold">{m.label}</text>
            {m.sublabel && (
              <text x={m.x} y={m.sublabelY} fontSize="9" fontFamily="monospace"
                    textAnchor="middle" fill="#8A8A93">{m.sublabel}</text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}

function WallMatrixImpl({ sp }) {
  // Empty-state for WAIT_OBSERVE (every row would be NEUTRAL — uninformative).
  if (sp.objective === "WAIT_OBSERVE") {
    return (
      <div data-testid="gex-strike-path-wall-matrix"
           className="bg-[#121216] border border-[#26262B] rounded-sm p-3">
        <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#8A8A93] mb-1">
          Wall × Objective Matrix
        </div>
        <div className="text-[10px] font-mono text-[#5F5F66] py-2">
          No active cycle objective — pick one to see how each wall affects your goal.
        </div>
      </div>
    );
  }
  const seen = new Set();
  const rows = [];
  const push = (label, w) => {
    const key = `${w.strike}-${w.type}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({ label, gex_sign: w.gex_sign, family: w.family, narrative: w.narrative });
  };
  if (sp.primary_put_wall) push(`$${sp.primary_put_wall.strike} Put Wall`, sp.primary_put_wall);
  (sp.path_walls || []).forEach(w => push(
    `$${w.strike} ${w.type === "CALL_WALL" ? "Call Wall" : "Put Wall"} (path)`, w,
  ));
  if (sp.primary_call_wall) push(`$${sp.primary_call_wall.strike} Call Wall`, sp.primary_call_wall);
  if (rows.length === 0) {
    return null;
  }
  return (
    <div data-testid="gex-strike-path-wall-matrix"
         className="bg-[#121216] border border-[#26262B] rounded-sm p-3">
      <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#8A8A93] mb-2">
        Wall × Objective Matrix
      </div>
      <table className="w-full text-[10px] font-mono">
        <thead>
          <tr className="text-[9px] uppercase tracking-[0.12em] text-[#5F5F66] border-b border-[#26262B]">
            <th className="text-left pb-1 pr-2 font-medium">Wall</th>
            <th className="text-left pb-1 pr-2 font-medium">Sign</th>
            <th className="text-left pb-1 font-medium">Effect on Objective</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[#1A1A20] last:border-0">
              <td className="text-white py-1 pr-2">{r.label}</td>
              <td className="py-1 pr-2" style={{ color: r.gex_sign === "POSITIVE" ? "#00FF88" : r.gex_sign === "NEGATIVE" ? "#FF3366" : "#8A8A93" }}>
                {r.gex_sign === "POSITIVE" ? "+GEX" : r.gex_sign === "NEGATIVE" ? "-GEX" : "≈0"}
              </td>
              <td className="py-1" style={{ color: FAMILY_COLOR[r.family] }}>
                {r.narrative}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const OperatorCard = React.memo(OperatorCardImpl, _spEqual);
const StrikePathMap = React.memo(StrikePathMapImpl, _spEqual);
const WallMatrix    = React.memo(WallMatrixImpl,    _spEqual);

export default function GEXStrikePathCard({ data, objective, onObjectiveChange, loading }) {
  const sp = data && data.strike_path;
  if (!sp) {
    return (
      <div data-testid="gex-strike-path-empty"
           className="bg-[#121216] border border-[#26262B] rounded-sm p-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93] mb-1">
          GEX Strike Path
        </div>
        <div className="text-[10px] font-mono text-[#5F5F66]">
          Computing… load a symbol with an active CSP/CC to see the cycle path.
        </div>
      </div>
    );
  }
  return (
    <div data-testid="gex-strike-path-card" className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-[9px] uppercase tracking-[0.15em] text-[#8A8A93]">Objective:</span>
        <select data-testid="gex-strike-path-objective-select"
                value={objective || sp.objective}
                onChange={e => onObjectiveChange?.(e.target.value)}
                disabled={loading}
                className="bg-[#08080A] border border-[#26262B] focus:border-[#3BB5FF] disabled:opacity-60 text-white rounded-sm px-2 py-1 text-[10px] font-mono uppercase tracking-[0.1em]">
          {OBJECTIVE_OPTIONS.map(o => (<option key={o.v} value={o.v}>{o.label}</option>))}
        </select>
        {loading && (
          <span data-testid="gex-strike-path-loading" className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#3BB5FF] animate-pulse">
            updating…
          </span>
        )}
      </div>
      <OperatorCard sp={sp} />
      <StrikePathMap sp={sp} />
      <WallMatrix sp={sp} />
    </div>
  );
}
